import { Box, Typography, IconButton, Menu, MenuItem, ListItemText } from '@mui/material';
import { MoreVert, ChevronRight } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { NotificationByUser } from 'types/notification';
import { getNotificationIcon, getStatusDotColor, formatTime } from './utils/notificationUtils';

interface SystemNotificationItemProps {
    notification: NotificationByUser;
    onMarkAsRead: (id: number) => void;
    onTogglePin?: (id: number) => void;
    onMute?: (id: number, module: string) => void;
    onCopyLink?: (link: string) => void;
}

export const SystemNotificationItem: React.FC<SystemNotificationItemProps> = ({
    notification,
    onMarkAsRead,
    onTogglePin,
    onMute,
    onCopyLink
}) => {
    const theme = useTheme();
    const isRead = notification.readAt !== null;
    const messageRef = useRef<HTMLDivElement>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const moreButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (messageRef.current) {
            const links = messageRef.current.querySelectorAll('a');
            links.forEach((link) => {
                if (link.href && !link.href.startsWith(window.location.origin)) {
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                }
            });
        }
    }, [notification.message.message]);

    const handleMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleCopyLink = () => {
        if (notification.recordLink && onCopyLink) {
            onCopyLink(notification.recordLink);
        } else if (onCopyLink) {
            onCopyLink(window.location.href);
        }
        handleMenuClose();
    };

    const handleToggleRead = () => {
        onMarkAsRead(notification.message.id);
        handleMenuClose();
    };

    const handleMute = () => {
        if (notification.module && onMute) {
            onMute(notification.message.id, notification.module);
        }
        handleMenuClose();
    };

    const handleTogglePin = () => {
        if (onTogglePin) {
            onTogglePin(notification.message.id);
        }
        handleMenuClose();
    };

    const relatedCount = notification.relatedCount ?? 0;

    return (
        <Box sx={{ position: 'relative' }} data-testid={`notification-item-${notification.message.id}`}>
            <Box
                sx={{
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center',
                    cursor: 'pointer',
                    position: 'relative'
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={() => !isRead && onMarkAsRead(notification.message.id)}
                data-testid={`notification-item-content-${notification.message.id}`}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        flex: 1,
                        backgroundColor: isHovered ? alpha(theme.palette.secondary.main, 0.08) : theme.palette.grey[50],
                        padding: '6px 8px',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s ease',
                        gap: '8px'
                    }}
                >
                    <Box data-testid={`notification-icon-${notification.message.id}`}>
                        {getNotificationIcon(notification.message.message, theme.palette.secondary.main)}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Box
                            ref={messageRef}
                            data-testid={`notification-message-${notification.message.id}`}
                            sx={{
                                color: theme.palette.text.primary,
                                fontSize: '14px',
                                lineHeight: '20px',
                                '& strong': {
                                    fontWeight: 600,
                                    color: theme.palette.text.primary
                                },
                                '& a': {
                                    color: theme.palette.primary.main,
                                    textDecoration: 'none',
                                    fontWeight: 500,
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                },
                                '& p': {
                                    margin: 0
                                }
                            }}
                            dangerouslySetInnerHTML={{ __html: notification.message.message }}
                        />

                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Typography
                                data-testid={`notification-time-${notification.message.id}`}
                                sx={{
                                    color: theme.palette.text.secondary,
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {formatTime(notification.message.createdAt)}
                            </Typography>
                            <Box
                                data-testid={`notification-status-dot-${notification.message.id}`}
                                sx={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: getStatusDotColor(
                                        isRead,
                                        notification.important || false,
                                        notification.message.priority,
                                        theme
                                    ),
                                    flexShrink: 0
                                }}
                            />
                            {(isHovered || Boolean(anchorEl)) && (
                                <IconButton
                                    ref={moreButtonRef}
                                    size="small"
                                    onClick={handleMoreClick}
                                    data-testid={`notification-more-button-${notification.message.id}`}
                                    sx={{
                                        padding: '4px',
                                        color: theme.palette.text.secondary,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.main,
                                            color: theme.palette.primary.contrastText
                                        }
                                    }}
                                >
                                    <MoreVert sx={{ fontSize: '18px' }} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexShrink: 0,
                        alignSelf: 'stretch',
                        backgroundColor: isHovered ? alpha(theme.palette.secondary.main, 0.08) : theme.palette.grey[50],
                        padding: '4px 16px',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s ease'
                    }}
                >
                    <Typography
                        data-testid={`notification-related-count-${notification.message.id}`}
                        sx={{
                            color: theme.palette.primary.main,
                            fontSize: '12px',
                            height: '100%',
                            fontWeight: 400,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            cursor: 'pointer',
                            '&:hover': {
                                color: theme.palette.primary.main
                            }
                        }}
                    >
                        +{relatedCount} &gt;
                    </Typography>
                </Box>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                data-testid={`notification-menu-${notification.message.id}`}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                PaperProps={{
                    sx: {
                        minWidth: 300,
                        borderRadius: '4px',
                        boxShadow: theme.shadows[4],
                        marginTop: '4px',
                        '& .MuiMenuItem-root': {
                            padding: '6px 16px',
                            fontSize: '14px',
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.secondary.main, 0.08)
                            },
                            '&:not(:last-child)': {
                                borderBottom: `1px solid ${theme.palette.primary.light}`
                            }
                        }
                    }
                }}
            >
                <MenuItem onClick={handleCopyLink} data-testid={`notification-menu-copy-link-${notification.message.id}`}>
                    <ListItemText primary="Copy link of the record" />
                </MenuItem>
                <MenuItem onClick={handleToggleRead} data-testid={`notification-menu-mark-read-${notification.message.id}`}>
                    <ListItemText primary={isRead ? 'Mark unread' : 'Mark read'} />
                </MenuItem>
                {notification.module && (
                    <MenuItem onClick={handleMute} data-testid={`notification-menu-mute-${notification.message.id}`}>
                        <ListItemText primary={`Mute this approval notification for "${notification.module}" module`} />
                    </MenuItem>
                )}
                <MenuItem data-testid={`notification-menu-data-focus-${notification.message.id}`}>
                    <ListItemText primary="Open Data Focus View" />
                    <ChevronRight sx={{ fontSize: '18px', color: theme.palette.text.secondary }} />
                </MenuItem>
                <MenuItem data-testid={`notification-menu-history-${notification.message.id}`}>
                    <ListItemText primary="Open Full Record History" />
                </MenuItem>
                <MenuItem onClick={handleTogglePin} data-testid={`notification-menu-pin-${notification.message.id}`}>
                    <ListItemText primary="Pin this notification" />
                </MenuItem>
            </Menu>
        </Box>
    );
};
